<template>
  <div
      class="cart-widget__overlay"
      :class="{ show }"
  />
  <div
      class="cart-widget"
      ref="root"
      :class="{ show }"
  >
    <div class="cart-widget__headline">
      <div class="cart-widget__headline-label">
        Dein Warenkorb
      </div>
      <div class="cart-widget__headline-close">
        <button
            type="button"
            title="Warenkorb schließen"
            @click="toggle"
        >
          <FontAwesomeIcon icon="fa-light fa-xmark"/>
          <span class="sr-only">
              Warenkorb schließen
          </span>
        </button>
      </div>
    </div>
    <div
        ref="content"
        class="cart-widget__content"
    >
      <AppCartItemRemove
          v-if="itemToRemove"
          :product="itemToRemove"
          :is-order-cancel="isOrderCancel"
          :item-key="keyToRemove"
          @clear="onRemoveDecline"
          @approved="onRemoveApproved"
      />
      <p v-if="countItemsAll === 0">
        Dein Warenkorb ist leer
      </p>
      <AppCartItems
          v-if="!itemToRemove"
          @remove="onItemRemove"
      />
    </div>
    <AppCartOverview
        ref="overview"
        v-if="countItemsAll > 0"
        @close="toggle"
    />
    <div
        v-if="isLoading"
        class="cart-loader"
    >
      <div>
        <FontAwesomeIcon icon="fa-light fa-spinner" class="fa-pulse"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import AppCartOverview from '@/components/cart/AppCartOverview.vue';
import AppCartItems from '@/components/cart/AppCartItems.vue';
import AppCartItemRemove from '@/components/cart/AppCartItemRemove.vue';
import Product from '@/classes/Product';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';

library.add(faSpinner, faXmark);

const store = useStore();
const router = useRouter();
const root = ref<InstanceType<typeof HTMLElement>>();
const content = ref<InstanceType<typeof HTMLElement>>();
const overview = ref<InstanceType<typeof CartOverview>>();

const itemToRemove = ref<Product | null>(null);
const keyToRemove = ref<string | null>(null);

const toggle = () => {
  store.commit('cart/TOGGLE_SHOW_WIDGET');
};

const show = computed(() => store.getters['cart/showWidget']);
const isLoading = computed(() => store.getters['cart/isLoading']);
const countItemsAll = computed(() => store.getters['cart/countAll']);
const isOrderPage = computed(() => store.getters['page/pageType'] === 'order');
const isOrderCancel = computed(() => countItemsAll.value === 1 && isOrderPage.value);
const orderIsCanceled = computed(() => countItemsAll.value === 0 && isOrderPage.value);

const onItemRemove = (key: string, product: Product) => {
  itemToRemove.value = product;
  keyToRemove.value = key;
};

const onRemoveDecline = () => {
  itemToRemove.value = null;
  keyToRemove.value = null;
};

const onRemoveApproved = () => {
  if (orderIsCanceled.value) {
    router.push('/');
    toggle();
  }
  onRemoveDecline();
};

watch(() => show.value, () => {
  if (!show.value) {
    setTimeout(() => {
      itemToRemove.value = null;
      keyToRemove.value = null;
    }, 475);
  }
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.cart-widget {
  position: fixed;
  right: 0;
  pointer-events: none;
  width: 540px;
  max-width: 100%;
  background-color: #FFFFFF;
  z-index: 999;
  @include responsive-size(16);
  box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
  border-radius: 0 0 20px 20px;
  top: var(--app-header-height-transformed, 0);
  height: auto;
  min-height: 515px;
  max-height: calc(100vh - var(--app-header-height-transformed, 0) - 100px);
  transition: top .5s linear, height .5s linear, transform .475s ease-out;
  overflow: hidden;
  transform: translateY(calc(-100% - 6px));

  @media (orientation: landscape) and (max-width: 1564px) {
    z-index: 99999 !important;
    top: 0 !important;
    min-height: 100% !important;
  }

  &.show {
    pointer-events: all;
    transform: translateY(0%);
  }

  @media (min-width: map_get($grid-breakpoints, 'lg')) {
    top: calc(var(--app-header-height-transformed, 0) + 40px);
    height: calc(100vh - var(--app-header-height-transformed, 0) - 250px);
    min-height: unset;
    max-height: unset;
    transform: translateX(calc(100% + 6px));
    border-radius: 40px 0 0 40px;

    &.show {
      pointer-events: all;
      transform: translateX(0%);
    }
  }

  @media (min-width: 1566px) {
    height: calc(100vh - var(--app-header-height-transformed, 0) - 80px);
  }

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr auto;

  &__headline {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #ECF2F6;
    overflow: visible;
    position: relative;
    z-index: 4;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      padding: 30px 40px;
    }

    &:after {
      content: '';
      height: 30px;
      display: block;
      position: absolute;
      background: linear-gradient(180deg, #fff, rgba(255, 255, 255, 0));
      width: 100%;
      top: calc(100% + 1px);
      left: 0;
    }

    &-label {
      font-size: 1.125rem;
      font-weight: 700;
      color: #3E3E3E;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        font-size: 1.5rem;
      }
    }

    &-close {
      line-height: 1;

      > button {
        font-size: 1.125rem;
        border: 0;
        background: transparent;
        padding: 0;
        outline: 0;
        color: #F93F4D;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: 1.75rem;
        }
      }
    }
  }

  &__content {
    @include font-regular;
    padding: 20px;
    overflow: auto;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      padding: 30px 40px;
    }
  }

  > .io-sidebar-group {
    > a {
      border-bottom: 1px solid #F7F7F7;
    }

    &:last-child {
      > a {
        border-bottom: 0;
      }
    }
  }

  .cart-loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background: transparentize(#F7F7F7, .45);
    box-shadow: 0 0 6px inset rgba(0, 0, 0, .16);

    > div {
      position: absolute;
      height: auto;
      width: auto;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      @include responsive-size-xl(60);
      color: $body-color;
    }

    i, svg {
      color: #38383A;
    }
  }
}

.cart-widget__overlay {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: #293033;
  opacity: 0;
  transition: opacity .475s ease-out;
  z-index: 900;
  pointer-events: none;

  &.show{
    opacity: 0.2;
  }
}
</style>
